import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DemoLayout from "@/components/demo/DemoLayout";
import { useTheme } from "@/contexts/ThemeContext";
import { ExternalLink, ChevronDown, ChevronUp, Info, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ScatterChart, Scatter, Cell, ReferenceLine,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;
const mono = { fontFamily: "var(--font-mono)" };
const soon = (label) => toast.info(`${label}`, { description: "Delivered on assessment completion" });

/* ── theme-aware tokens ── */
const crd = { background: "var(--surface-card)", border: "var(--border-width) solid var(--border-default)", borderRadius: "1rem", boxShadow: "var(--shadow-card)", transition: "background 150ms ease, border-color 150ms ease" };
const inner = { background: "var(--surface-inner)", borderRadius: "0.5rem" };
const track = { background: "var(--surface-muted)", borderRadius: "9999px" };
const tp = { color: "var(--text-primary)" };
const ts = { color: "var(--text-secondary)" };
const tm = { color: "var(--text-muted)" };
const bdr = { borderColor: "var(--border-default)" };

const scoreColor = (v) => !v && v !== 0 ? "var(--text-muted)" : v >= 3.5 ? "#22c55e" : v >= 2.5 ? "#f59e0b" : "#ef4444";

/* ═══ INSIGHT CARDS ═══ */
function InsightCards({ cards }) {
  const sevStyles = {
    critical: "bg-red-500/10 border-red-500/30",
    warning: "bg-amber-500/10 border-amber-500/30",
    positive: "bg-green-500/10 border-green-500/30",
    info: "bg-blue-500/10 border-blue-500/30",
  };
  const sevIcon = { critical: "\uD83D\uDEA8", warning: "\uD83D\uDEAA", positive: "\uD83D\uDCC8", info: "\uD83D\uDCA1" };

  if (!cards?.length) return null;
  return (
    <div className="mb-6" data-testid="insight-cards">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold" style={tp}>Strategic Insights</p>
        <span className="text-[10px] px-2 py-0.5 rounded" style={{ ...inner, ...tm }}>Auto-generated</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {cards.slice(0, 6).map((c, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            onClick={() => soon(c.action_label)}
            className={`border rounded-xl p-4 cursor-pointer hover:scale-[1.01] transition-transform ${sevStyles[c.severity] || sevStyles.info}`}>
            <div className="flex items-start gap-2.5">
              <span className="text-lg">{sevIcon[c.severity] || "\u2139"}</span>
              <div className="min-w-0">
                <p className="text-[10px]" style={tm}>{c.title}</p>
                <p className="text-sm font-semibold truncate" style={tp}>{c.subtitle}</p>
              </div>
            </div>
            <p className="text-xs mt-2 line-clamp-2" style={ts}>{c.message}</p>
            <p className="text-xs text-blue-500 mt-2 font-medium">{c.action_label} &rarr;</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ═══ REGIONAL REPORT CARDS ═══ */
function RegionalCards({ regions, sentiment, isDark }) {
  const [selected, setSelected] = useState(null);

  const getHealth = (r) => {
    if (r.assessed === 0) return { label: "Not Started", cls: "text-gray-500 bg-gray-500/15" };
    if (r.completion_rate >= 70) return { label: "On Track", cls: "text-green-500 bg-green-500/15" };
    if (r.completion_rate >= 30) return { label: "In Progress", cls: "text-amber-500 bg-amber-500/15" };
    return { label: "Critical", cls: "text-red-500 bg-red-500/15" };
  };

  const avgScore = (scores) => {
    const vals = Object.values(scores).filter(v => v != null);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };

  const topSkill = (scores) => {
    let best = null;
    for (const [k, v] of Object.entries(scores)) {
      if (v != null && (!best || v > best[1])) best = [k, v];
    }
    return best;
  };

  const dimNames = { role_skills: "Role Skills", domain_knowledge: "Domain Knowledge", tool_proficiency: "Tool Proficiency", mgr_relationship: "Mgr Relationship", org_satisfaction: "Org Satisfaction" };

  return (
    <div className="mb-6" data-testid="regional-cards">
      <p className="text-sm font-semibold mb-3" style={tp}>Regional Assessment Status</p>
      <div className="grid grid-cols-6 gap-3 mb-3">
        {regions.map(r => {
          const h = getHealth(r);
          const avg = avgScore(r.scores);
          const isActive = selected?.code === r.code;
          return (
            <motion.div key={r.code} whileHover={{ scale: 1.02 }}
              onClick={() => setSelected(isActive ? null : r)}
              className={`p-4 rounded-xl cursor-pointer transition-all border ${isActive ? "border-blue-500 ring-1 ring-blue-500/30" : ""}`}
              style={{ ...crd, ...(isActive ? { borderColor: "#3b82f6" } : {}) }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xl">{r.flag}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${h.cls}`}>{h.label}</span>
              </div>
              <p className="text-sm font-semibold" style={tp}>{r.name}</p>
              <p className="text-lg font-bold mt-1" style={{ ...mono, color: r.completion_rate >= 70 ? "#22c55e" : r.completion_rate >= 30 ? "#f59e0b" : r.completion_rate > 0 ? "#ef4444" : "var(--text-muted)" }}>
                {r.completion_rate}%
              </p>
              <p className="text-[10px] mt-0.5" style={tm}>{r.assessed} of {r.total} completed</p>
              {avg && (
                <div className="mt-2 pt-2 border-t" style={bdr}>
                  <p className="text-[9px]" style={tm}>Avg Skill Score</p>
                  <p className="text-sm font-semibold" style={{ ...mono, color: scoreColor(parseFloat(avg)) }}>{avg}</p>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
      {/* Expanded region report */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden">
            <div className="p-6 rounded-xl" style={crd}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{selected.flag}</span>
                  <div>
                    <p className="text-base font-semibold" style={tp}>{selected.name} &mdash; Regional Report</p>
                    <p className="text-xs" style={ts}>{selected.assessed} of {selected.total} assessments completed ({selected.completion_rate}%)</p>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="text-xs px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80" style={{ ...inner, ...ts, borderColor: "var(--border-default)" }}>
                  Close
                </button>
              </div>
              {/* Progress bar */}
              <div className="mb-5">
                <div className="flex justify-between text-[10px] mb-1" style={tm}>
                  <span>Completion Progress</span>
                  <span style={mono}>{selected.assessed}/{selected.total}</span>
                </div>
                <div className="h-2.5 rounded-full overflow-hidden" style={track}>
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${selected.completion_rate}%`,
                    background: selected.completion_rate >= 70 ? "linear-gradient(90deg, #22c55e, #4ade80)" : selected.completion_rate >= 30 ? "linear-gradient(90deg, #f59e0b, #fbbf24)" : "linear-gradient(90deg, #ef4444, #f87171)",
                  }} />
                </div>
              </div>
              {/* Skills breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold mb-3" style={tp}>Skills Breakdown</p>
                  {selected.assessed >= 5 ? (
                    <div className="space-y-2.5">
                      {Object.entries(selected.scores).map(([k, v]) => (
                        <div key={k}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs" style={ts}>{dimNames[k] || k}</span>
                            {v != null ? (
                              <span className="text-sm font-semibold" style={{ ...mono, color: scoreColor(v) }}>{v.toFixed(1)}</span>
                            ) : (
                              <span className="text-[10px] italic" style={tm}>Awaiting data</span>
                            )}
                          </div>
                          <div className="h-1.5 rounded-full overflow-hidden" style={track}>
                            {v != null && (
                              <div className="h-full rounded-full" style={{ width: `${(v / 5) * 100}%`, backgroundColor: scoreColor(v) }} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg text-center" style={inner}>
                      <p className="text-xs" style={tm}>Minimum 5 completed assessments required for skills data</p>
                      <p className="text-sm font-semibold mt-1" style={tp}>{Math.max(0, 5 - selected.assessed)} more needed</p>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold mb-3" style={tp}>Key Metrics</p>
                  <div className="space-y-2">
                    {(() => {
                      const top = topSkill(selected.scores);
                      const scoredDims = Object.entries(selected.scores).filter(([, v]) => v != null);
                      const weakest = scoredDims.length ? scoredDims.reduce((a, b) => a[1] < b[1] ? a : b) : null;
                      return (
                        <>
                          <div className="p-3 rounded-lg" style={inner}>
                            <p className="text-[10px]" style={tm}>Strongest Dimension</p>
                            {top ? (
                              <p className="text-sm font-semibold text-green-500">{dimNames[top[0]]} ({top[1].toFixed(1)})</p>
                            ) : (
                              <p className="text-xs italic" style={tm}>Not enough data</p>
                            )}
                          </div>
                          <div className="p-3 rounded-lg" style={inner}>
                            <p className="text-[10px]" style={tm}>Needs Attention</p>
                            {weakest && weakest[1] < 3.5 ? (
                              <p className="text-sm font-semibold" style={{ color: scoreColor(weakest[1]) }}>{dimNames[weakest[0]]} ({weakest[1].toFixed(1)})</p>
                            ) : weakest ? (
                              <p className="text-sm font-semibold text-green-500">All dimensions strong</p>
                            ) : (
                              <p className="text-xs italic" style={tm}>Not enough data</p>
                            )}
                          </div>
                          <div className="p-3 rounded-lg" style={inner}>
                            <p className="text-[10px]" style={tm}>Remaining Assessments</p>
                            <p className="text-sm font-semibold" style={tp}>{selected.total - selected.assessed} pending</p>
                          </div>
                          <div className="p-3 rounded-lg" style={inner}>
                            <p className="text-[10px]" style={tm}>Data Coverage</p>
                            <p className="text-sm font-semibold" style={tp}>{scoredDims.length} of 5 dimensions</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══ SKILLS MATRIX (Coming Soon) ═══ */
function SkillsMatrix({ data }) {
  // Placeholder scores for the blurred preview
  const dims = data.dimensions || [];
  const regions = data.regions || [];

  return (
    <div style={crd} className="p-6 mb-6 relative overflow-hidden" data-testid="skills-matrix">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold" style={tp}>Skills Proficiency by Region</p>
          <p className="text-xs mt-0.5" style={ts}>Detailed skills breakdown across all assessment dimensions</p>
        </div>
        <div className="flex gap-3">
          {Object.entries(data.legend).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: v.color }} />
              <span className="text-[10px]" style={tm}>{v.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Blurred table preview */}
      <div className="relative">
        <div className="blur-[6px] opacity-40 select-none pointer-events-none">
          <table className="w-full">
            <thead>
              <tr style={bdr} className="border-b">
                <th className="text-left py-3 px-2 text-[10px] uppercase tracking-wider font-medium" style={tm}>Dimension</th>
                {regions.map(r => (
                  <th key={r.code} className="text-center py-3 px-4 text-[10px] uppercase tracking-wider font-medium" style={tm}>
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-base">{r.flag}</span>
                      <span>{r.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dims.map(dim => (
                <tr key={dim.key} className="border-b border-opacity-30" style={bdr}>
                  <td className="py-3.5 px-2 text-sm" style={tp}>{dim.name}</td>
                  {regions.map(r => (
                    <td key={r.code} className="py-3.5 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-12 h-7 rounded-lg text-sm font-semibold"
                        style={{ ...mono, backgroundColor: "#22c55e18", color: "#22c55e" }}>
                        3.8
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl mb-3" style={{ background: "var(--surface-muted)" }}>
            <Lock className="w-6 h-6" style={tm} />
          </div>
          <p className="text-sm font-semibold" style={tp}>Awaiting Assessment Data</p>
          <p className="text-xs mt-1 max-w-sm text-center" style={ts}>
            Skills proficiency scores will populate as more assessments are completed across all regions.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══ SENTIMENT TRENDS ═══ */
function SentimentTrends({ data, isDark }) {
  const [selected, setSelected] = useState(["fair_treatment", "work_life_balance", "career_growth"]);
  const colors = { role_clarity: "#22c55e", belonging: "#06b6d4", team_morale: "#a855f7",
    career_growth: "#f59e0b", work_life_balance: "#3b82f6", leadership_confidence: "#8b5cf6",
    innovation_support: "#14b8a6", communication: "#6366f1", fair_treatment: "#ef4444" };
  const gridClr = isDark ? "#1f1f1f" : "#e5e7eb";

  const toggle = (k) => setSelected(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  return (
    <div style={crd} className="p-6" data-testid="sentiment-trends">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold" style={tp}>Organizational Sentiment Trends</p>
          <p className="text-xs mt-0.5" style={ts}>Toggle dimensions below &middot; Hover for detail</p>
        </div>
      </div>
      {/* Dimension toggles */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {data.current.map(d => (
          <button key={d.key} onClick={() => toggle(d.key)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border ${
              selected.includes(d.key) ? "border-blue-500/50 text-blue-400 bg-blue-500/15" : "border-transparent"
            }`} style={!selected.includes(d.key) ? { ...inner, ...ts } : {}}>
            {d.name}
          </button>
        ))}
      </div>
      {/* Chart */}
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.trend_data} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridClr} />
            <XAxis dataKey="week" tick={{ fill: isDark ? "#6b7280" : "#9ca3af", fontSize: 10 }} stroke={gridClr} />
            <YAxis domain={[2, 5]} tick={{ fill: isDark ? "#6b7280" : "#9ca3af", fontSize: 10 }} stroke={gridClr} />
            <Tooltip contentStyle={{ background: isDark ? "#111" : "#fff", border: `1px solid ${gridClr}`, borderRadius: 8, fontSize: 11, color: isDark ? "#fff" : "#111" }} />
            <ReferenceLine y={3.5} stroke="#22c55e" strokeDasharray="5 5" label={{ value: "Strong", fill: "#22c55e", fontSize: 9 }} />
            {selected.map(k => (
              <Line key={k} type="monotone" dataKey={k} stroke={colors[k] || "#888"} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Current values */}
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t" style={bdr}>
        {data.current.filter(d => selected.includes(d.key)).map(d => (
          <div key={d.key} className="flex items-center justify-between p-2 rounded-lg" style={inner}>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors[d.key] }} />
              <span className="text-[11px]" style={ts}>{d.name}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold" style={{ ...mono, color: scoreColor(d.score) }}>{d.score.toFixed(2)}</span>
              <span className={`text-[10px] ${d.change > 0 ? "text-green-500" : d.change < 0 ? "text-red-500" : ""}`} style={d.change === 0 ? tm : {}}>
                {d.change > 0 ? "\u2191" : d.change < 0 ? "\u2193" : "\u2192"}{Math.abs(d.change).toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>
      {/* Alerts */}
      {data.alerts?.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {data.alerts.map((a, i) => (
            <div key={i} className={`p-2.5 rounded-lg border ${a.type === "critical" ? "bg-red-500/10 border-red-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={tp}>{a.type === "critical" ? "\uD83D\uDEA8" : "\u26A0\uFE0F"} {a.message}</span>
                <span className="text-[10px] text-blue-500">{a.action}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══ ASPIRATION VS CAPABILITY MATRIX ═══ */
function AspirationMatrix({ data }) {
  const quads = [
    { key: "unrealistic", cls: "bg-red-500/10 border-red-500/30", txtCls: "text-red-500" },
    { key: "ready_now", cls: "bg-green-500/10 border-green-500/30", txtCls: "text-green-500" },
    { key: "disengaged", cls: "bg-gray-500/10 border-gray-500/30", txtCls: "" },
    { key: "hidden_gem", cls: "bg-blue-500/10 border-blue-500/30", txtCls: "text-blue-500" },
  ];

  return (
    <div style={crd} className="p-6" data-testid="aspiration-matrix">
      <p className="text-sm font-semibold mb-1" style={tp}>Aspiration vs Capability</p>
      <p className="text-xs mb-4" style={ts}>Click any quadrant to drill down</p>
      <div className="grid grid-cols-2 gap-3">
        {quads.map(q => {
          const d = data.matrix[q.key];
          return (
            <motion.div key={q.key} whileHover={{ scale: 1.02 }}
              onClick={() => soon(`${d.label} — ${d.count} employees`)}
              className={`border rounded-xl p-4 cursor-pointer ${q.cls}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] font-semibold uppercase ${q.txtCls}`} style={!q.txtCls ? ts : {}}>{d.label}</span>
                <span className={`text-2xl font-bold ${q.txtCls}`} style={{ ...mono, ...(q.txtCls ? {} : ts) }}>{d.count}</span>
              </div>
              <p className="text-[11px]" style={tm}>{d.description}</p>
            </motion.div>
          );
        })}
      </div>
      <div className="flex justify-between mt-3 text-[9px]" style={tm}>
        <span>&larr; Low Capability</span><span>High Capability &rarr;</span>
      </div>
      <div className="text-center mt-1 text-[9px]" style={tm}>&uarr; High Aspiration | Low Aspiration &darr;</div>
    </div>
  );
}

/* ═══ CALIBRATION SCATTER ═══ */
function CalibrationAnalysis({ data, isDark, navigate }) {
  const [clickedDot, setClickedDot] = React.useState(null);
  const typeClr = { under: "#3b82f6", aligned: "#71767b", over: "#ef4444" };
  const gridClr = isDark ? "#1f1f1f" : "#e5e7eb";
  const typeLabel = { under: "Hidden Talent (Under-rater)", aligned: "Well-Calibrated", over: "Coaching Needed (Over-rater)" };

  return (
    <div style={crd} className="p-6 relative" data-testid="calibration-analysis">
      {/* Blurred overlay on dot click */}
      {clickedDot && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center rounded-xl cursor-pointer"
          style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", background: isDark ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.6)" }}
          onClick={() => setClickedDot(null)}
          data-testid="talent-map-overlay"
        >
          <div className="text-center p-6 rounded-xl" style={{ background: isDark ? "rgba(30,30,30,0.95)" : "rgba(255,255,255,0.95)", border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, maxWidth: 320 }}>
            <div className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: (typeClr[clickedDot.type] || "#666") + "20" }}>
              <Lock className="w-5 h-5" style={{ color: typeClr[clickedDot.type] || "#666" }} />
            </div>
            <p className="text-sm font-semibold mb-1" style={tp}>{typeLabel[clickedDot.type] || "Employee"}</p>
            <p className="text-xs mb-1" style={ts}>SA: {clickedDot.sa?.toFixed(1)} · MV: {clickedDot.mv?.toFixed(1)}</p>
            <p className="text-[11px] mt-2 px-3 py-1.5 rounded-lg" style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", color: isDark ? "#9ca3af" : "#6b7280" }}>
              Available after assessment completion
            </p>
            <p className="text-[10px] mt-2" style={tm}>Click anywhere to dismiss</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold" style={tp}>SA vs MV Gap Analysis</p>
          <p className="text-xs mt-0.5" style={ts}>{data.summary.total_with_both} employees with both SA and MV</p>
        </div>
        <button onClick={() => navigate("/executive/demo/heatmap")} className="text-[10px] text-blue-500 hover:underline flex items-center gap-1">
          Full Heatmap <ExternalLink className="w-3 h-3" />
        </button>
      </div>
      <div className="h-[200px] rounded-lg p-2" style={inner}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridClr} />
            <XAxis type="number" dataKey="sa" domain={[1, 5]} tick={{ fill: isDark ? "#6b7280" : "#9ca3af", fontSize: 10 }} stroke={gridClr}
              label={{ value: "Self-Assessment \u2192", position: "bottom", fill: isDark ? "#6b7280" : "#9ca3af", fontSize: 9 }} />
            <YAxis type="number" dataKey="mv" domain={[1, 5]} tick={{ fill: isDark ? "#6b7280" : "#9ca3af", fontSize: 10 }} stroke={gridClr}
              label={{ value: "Manager \u2192", angle: -90, position: "left", fill: isDark ? "#6b7280" : "#9ca3af", fontSize: 9 }} />
            <Tooltip contentStyle={{ background: isDark ? "#111" : "#fff", border: `1px solid ${gridClr}`, borderRadius: 8, fontSize: 11, color: isDark ? "#fff" : "#111" }}
              formatter={(v, name) => [v.toFixed ? v.toFixed(1) : v, name === "sa" ? "Self" : "Manager"]} />
            <ReferenceLine segment={[{ x: 1, y: 1 }, { x: 5, y: 5 }]} stroke={gridClr} strokeDasharray="5 5" />
            <Scatter data={data.scatter_data} onClick={(dotData) => setClickedDot(dotData)}>
              {data.scatter_data.map((e, i) => <Cell key={i} fill={typeClr[e.type]} cursor="pointer" />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-3">
        {[
          { label: "Hidden Talent", v: data.summary.under_raters, cls: "bg-blue-500/10 border-blue-500/30", tc: "text-blue-500" },
          { label: "Aligned", v: data.summary.aligned, cls: "bg-gray-500/10 border-gray-500/30", tc: "" },
          { label: "Coaching Needed", v: data.summary.over_raters, cls: "bg-red-500/10 border-red-500/30", tc: "text-red-500" },
          { label: "Total", v: data.summary.total_with_both, cls: "", tc: "" },
        ].map(c => (
          <div key={c.label} className={`p-2.5 border rounded-lg text-center ${c.cls}`} style={!c.cls ? inner : {}}>
            <p className={`text-xl font-bold ${c.tc}`} style={{ ...mono, ...(c.tc ? {} : tp) }}>{c.v}</p>
            <p className="text-[9px]" style={tm}>{c.label}</p>
          </div>
        ))}
      </div>
      {data.insights?.under_raters && (
        <div className="mt-3 p-2.5 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
          <p className="text-xs text-blue-400"><strong>Hidden talent:</strong> {data.insights.under_raters.description} &mdash; {data.insights.under_raters.action}</p>
        </div>
      )}
    </div>
  );
}

/* ═══ FLIGHT RISK ═══ */
function FlightRisk({ data }) {
  return (
    <div style={crd} className="p-6" data-testid="flight-risk">
      <p className="text-sm font-semibold mb-4" style={tp}>Flight Risk Analysis</p>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: "High Risk", v: data.summary.high_risk, cls: "bg-red-500/10 border-red-500/30", tc: "text-red-500", sub: "Perf \u22654.0, Sent \u22642.5" },
          { label: "Moderate", v: data.summary.moderate_risk, cls: "bg-amber-500/10 border-amber-500/30", tc: "text-amber-500", sub: "Perf \u22653.5, Sent 2.5-3.5" },
          { label: "Low Risk", v: data.summary.low_risk, cls: "bg-green-500/10 border-green-500/30", tc: "text-green-500", sub: "Healthy" },
        ].map(r => (
          <div key={r.label} className={`p-3 border rounded-xl text-center ${r.cls}`}>
            <p className={`text-2xl font-bold ${r.tc}`} style={mono}>{r.v}</p>
            <p className="text-[10px] mt-0.5" style={ts}>{r.label}</p>
            <p className="text-[9px]" style={tm}>{r.sub}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => soon("High Risk Employee List")} className="flex-1 py-2 text-xs font-medium text-red-500 bg-red-500/15 border border-red-500/30 rounded-lg hover:bg-red-500/25 transition-colors">
          View High Risk List &rarr;
        </button>
        <button onClick={() => soon("Retention Plan Builder")} className="flex-1 py-2 text-xs font-medium rounded-lg border transition-colors" style={{ ...inner, ...tp, borderColor: "var(--border-default)" }}>
          Create Retention Plan
        </button>
      </div>
    </div>
  );
}

/* ═══ SKILL GAP ACTIONS ═══ */
function SkillGapActions({ gaps }) {
  const [expanded, setExpanded] = useState(null);
  if (!gaps?.length) return null;

  return (
    <div style={crd} className="p-6 mb-6" data-testid="skill-gap-actions">
      <p className="text-sm font-semibold mb-1" style={tp}>Skill Gap &rarr; Action Mapping</p>
      <p className="text-xs mb-4" style={ts}>Click to expand recommended actions with ROI estimates</p>
      <div className="space-y-3">
        {gaps.map((g, i) => (
          <div key={i} className="border rounded-xl overflow-hidden" style={bdr}>
            <button onClick={() => setExpanded(expanded === i ? null : i)}
              className="w-full p-4 flex items-center justify-between transition-colors" style={inner}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-red-500/15 flex items-center justify-center">
                  <span className="text-base font-bold text-red-500" style={mono}>{g.score}</span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold" style={tp}>{g.skill}</p>
                  <p className="text-[11px]" style={ts}>{g.affected_regions?.join(", ")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-[10px]" style={tm}>vs Industry</p>
                  <p className="text-sm text-red-500 font-semibold" style={mono}>{g.gap_vs_industry}</p>
                </div>
                {expanded === i ? <ChevronUp className="w-4 h-4" style={tm} /> : <ChevronDown className="w-4 h-4" style={tm} />}
              </div>
            </button>
            <AnimatePresence>
              {expanded === i && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <div className="px-4 pb-4 pt-3 border-t" style={bdr}>
                    <p className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={tm}>Recommended Actions</p>
                    <div className="space-y-2">
                      {g.recommended_actions?.map((a, j) => (
                        <div key={j} className="flex items-start gap-2.5 p-2.5 rounded-lg" style={crd}>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${
                            a.priority === "high" ? "bg-red-500/20 text-red-500" : "bg-amber-500/20 text-amber-500"}`}>
                            {j + 1}
                          </div>
                          <div>
                            <p className="text-xs" style={tp}>{a.action}</p>
                            <div className="flex gap-3 mt-1 text-[10px]" style={ts}>
                              <span>{a.duration}</span><span>{a.cost_estimate}</span>
                              <span className="text-green-500">{a.expected_improvement}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-[10px]" style={tm}>Total Investment</p>
                        <p className="text-base font-semibold" style={tp}>{g.total_investment}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px]" style={tm}>ROI Timeline</p>
                        <p className="text-sm text-green-500">{g.roi_timeline}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ BENCHMARKS ═══ */
function Benchmarks({ data }) {
  if (!data?.comparisons?.length) return null;
  return (
    <div style={crd} className="p-6 mb-6" data-testid="benchmarks">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold" style={tp}>Industry Benchmarks</p>
          <p className="text-xs mt-0.5" style={ts}>Source: {data.source}</p>
        </div>
      </div>
      <div className="space-y-3">
        {data.comparisons.map((c, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-36 text-xs" style={tp}>{c.skill}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={track}>
                  <div className="h-full rounded-full" style={{ width: `${(c.sagicor / 5) * 100}%`, backgroundColor: scoreColor(c.sagicor) }} />
                </div>
                <span className="text-xs w-7 text-right font-semibold" style={{ ...mono, color: scoreColor(c.sagicor) }}>{c.sagicor}</span>
              </div>
              <div className="flex gap-3 text-[9px]" style={tm}>
                <span>Insurance: {c.insurance_industry}</span><span>Tech: {c.tech_industry}</span>
              </div>
            </div>
            <span className={`text-sm font-semibold ${c.gap >= 0 ? "text-green-500" : "text-red-500"}`} style={mono}>
              {c.gap >= 0 ? "+" : ""}{c.gap}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ MAIN PAGE ═══ */
export default function DemoIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    try {
      const cached = localStorage.getItem("dash_intelligence");
      if (cached) { setData(JSON.parse(cached)); setLoading(false); }
    } catch {}
    axios.get(`${API}/live-intelligence`)
      .then(r => {
        setData(r.data);
        try { localStorage.setItem("dash_intelligence", JSON.stringify(r.data)); } catch {}
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <DemoLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-sm" style={ts}>Loading Live Intelligence...</div>
        </div>
      </DemoLayout>
    );
  }

  return (
    <DemoLayout>
      <div className="space-y-0" data-testid="intelligence-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold" style={tp}>Live Intelligence</h1>
          <p className="text-xs mt-0.5" style={ts}>
            Real insights from {data.summary.sa_completed}+ completed assessments &middot; Last updated: {new Date(data.summary.last_updated).toLocaleString()}
          </p>
        </div>

        {/* Insight Cards */}
        <InsightCards cards={data.insight_cards} />

        {/* Regional Report Cards */}
        <RegionalCards regions={data.skills_by_region.regions} sentiment={data.sentiment} isDark={isDark} />

        {/* Skills Matrix */}
        <SkillsMatrix data={data.skills_by_region} />

        {/* Sentiment + Aspiration (2-col) */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <SentimentTrends data={data.sentiment} isDark={isDark} />
          <AspirationMatrix data={data.aspiration_capability} />
        </div>

        {/* Calibration + Flight Risk (2-col) */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <CalibrationAnalysis data={data.calibration} isDark={isDark} navigate={navigate} />
          <FlightRisk data={data.flight_risk} />
        </div>

        {/* Skill Gap Actions */}
        <SkillGapActions gaps={data.skill_gap_actions} />

        {/* Benchmarks */}
        <Benchmarks data={data.benchmarks} />
      </div>
    </DemoLayout>
  );
}
